/**
 * Pure formatting + validation helpers for the Add Payment Account modal (CNP-133).
 * No React, no i18n — just input→output so they're trivially unit-testable. Bounds match
 * the legacy mdvipconnect product (card min-13, account 8-17, expiry not-past / ≤ +12yr).
 */
import { endOfMonth, isBefore } from 'date-fns';
import type { BillingAddressValues, CardNetwork } from './paymentAccount.types';

/** Longest supported PAN: 16 (all networks except Amex, which is 15). Display caps here. */
const MAX_PAN_DIGITS = 16;

/** Strip non-digits, cap length, and group for display. Amex (IIN 3) uses 4-6-5; others, blocks of 4. */
export function formatCardNumber(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, MAX_PAN_DIGITS);
  if (digits[0] === '3') {
    const amex = digits.slice(0, 15);
    return [amex.slice(0, 4), amex.slice(4, 10), amex.slice(10, 15)].filter(Boolean).join(' ');
  }
  return (digits.match(/.{1,4}/g) ?? []).join(' ');
}

/** Mask a raw expiry entry into "MM/YY" as the user types. */
export function formatExpiry(value: string): string {
  const digits = value.replace(/\D/g, '');
  // Insert the slash only once a year digit exists. Forcing it at exactly two digits traps
  // backspace (deleting the slash re-adds it), so wait for the third digit to add "MM/".
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}`;
}

/** Card network from the leading digits (sufficient for display + CVV-length rules). */
export function detectNetwork(cardNumber: string): CardNetwork {
  const digits = cardNumber.replace(/\D/g, '');
  switch (digits[0]) {
    case '4':
      return 'visa';
    case '5':
      return 'mastercard';
    case '3':
      return 'amex';
    case '6':
      return 'discover';
  }
  // 2-series Mastercard (BIN 2221–2720), once enough digits are present to decide.
  const firstFour = Number(digits.slice(0, 4));
  if (firstFour >= 2221 && firstFour <= 2720) return 'mastercard';
  return 'unknown';
}

/** Amex CVVs are 4 digits; every other network is 3. */
export function expectedCvvLength(network: CardNetwork): number {
  return network === 'amex' ? 4 : 3;
}

/** Amex PANs are 15 digits; every other network is 16 (per finance). */
export function expectedCardLength(network: CardNetwork): number {
  return network === 'amex' ? 15 : 16;
}

/**
 * Parse a well-formed "MM/YY" into numeric month + 4-digit year. The century is derived from
 * the clock rather than hardcoded (so it survives the 2100 rollover); cards are always-future,
 * so the current century is the correct base.
 */
export function parseExpiry(expiry: string, now: Date = new Date()): { month: number; year: number } {
  const [month, year] = expiry.split('/');
  const century = Math.floor(now.getFullYear() / 100) * 100;
  return { month: Number(month), year: century + Number(year) };
}

/**
 * Formats a numeric month + 4-digit year for display, e.g. "07/2026" — the saved-card display
 * format. Distinct from the 2-digit "MM/YY" `parseExpiry` reads, which is the format typed while
 * entering a new card's expiry.
 */
export function formatExpiryFromParts(month: number, year: number): string {
  return `${String(month).padStart(2, '0')}/${year}`;
}

/**
 * Formats a numeric month + 4-digit year as 2-digit "MM/YY" — the shape the editable expiry
 * `TextField` (and `parseExpiry`) expects. Used to seed the Edit form's initial value from a
 * saved card, which stores a 4-digit year; distinct from `formatExpiryFromParts`'s display format.
 */
export function formatExpiryForInput(month: number, year: number): string {
  return `${String(month).padStart(2, '0')}/${String(year).slice(-2)}`;
}

/** Structural shape {@link toSavedAddress} actually reads — satisfied by both `CreditCardAccount` and `BankAccountAccount`. */
export interface AddressableAccount {
  holderName: string;
  line1: string;
  line2: string;
  city: string;
  state: string;
  zip: string;
}

/**
 * Builds the Edit modal's saved billing address from a card or bank account — the Edit form seeds
 * from the account's own saved address (unlike Add, where the member profile address might be
 * entirely absent). Shared because a card and a bank account map to `BillingAddressValues`
 * identically; only the field's *source* differs, not the mapping.
 */
export function toSavedAddress(account: AddressableAccount): BillingAddressValues {
  return {
    name: account.holderName,
    line1: account.line1,
    line2: account.line2,
    city: account.city,
    state: account.state,
    zip: account.zip,
  };
}

/** Joins city/state/zip for display, dropping empty parts cleanly (billingAddress fields may be null). */
export function formatCityStateZip(city: string, state: string, zip: string): string {
  const stateZip = [state, zip].filter(Boolean).join(' ');
  return [city, stateZip].filter(Boolean).join(', ');
}

/** A card is usable through the end of its expiry month. */
export function isNotPast(expiry: string, now: Date = new Date()): boolean {
  const { month, year } = parseExpiry(expiry, now);
  const lastDayOfExpiryMonth = endOfMonth(new Date(year, month - 1, 1));
  return !isBefore(lastDayOfExpiryMonth, now);
}

/** Reject expiries more than 12 years out (legacy DV-6888 bound). */
export function isNotTooFarFuture(expiry: string, now: Date = new Date()): boolean {
  const { year } = parseExpiry(expiry, now);
  return year <= now.getFullYear() + 12;
}

/**
 * Validates a 9-digit ABA routing number: a recognized Federal Reserve prefix plus a valid
 * mod-10 weighted checksum. Mirrors the backend's `verify-bank-account.model.ts` rules exactly so
 * a bad routing number surfaces as an inline field error here instead of a generic error toast
 * after the round trip to `verifyBankAccountInformation` (whose ZodValidationPipe rejects it
 * before the domain Failure union, i.e. before a friendly per-field message is possible).
 */
export function isValidRoutingNumber(routingNumber: string): boolean {
  if (!/^\d{9}$/.test(routingNumber)) return false;

  const prefix = Number(routingNumber.slice(0, 2));
  const validPrefix =
    (prefix >= 1 && prefix <= 12) || (prefix >= 21 && prefix <= 32) || (prefix >= 61 && prefix <= 72) || prefix === 80;
  if (!validPrefix) return false;

  const weights = [3, 7, 1, 3, 7, 1, 3, 7, 1];
  const sum = weights.reduce((acc, weight, i) => acc + weight * Number(routingNumber[i]), 0);
  return sum % 10 === 0;
}
